import { Shield, Scan, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface LogoProps {
  className?: string;
  showText?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "white" | "dark";
}

export function Logo({ 
  className, 
  showText = true, 
  size = "md",
  variant = "default"
}: LogoProps) {
  const sizeClasses = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-12 w-12"
  };

  const textSizeClasses = {
    sm: "text-lg",
    md: "text-xl",
    lg: "text-2xl"
  };

  const iconColor = {
    default: "text-purple-600 dark:text-purple-400",
    white: "text-white",
    dark: "text-slate-900 dark:text-white"
  };

  const textColor = {
    default: "text-slate-900 dark:text-white",
    white: "text-white",
    dark: "text-slate-900 dark:text-white"
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="relative">
        {/* Glow effect */}
        <div className="absolute inset-0 bg-purple-600/20 rounded-xl blur-md animate-pulse"></div>
        {/* Main logo container */}
        <div className={cn(
          "relative flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700 p-1.5 shadow-lg",
          sizeClasses[size]
        )}>
          {/* Shield with scan lines */}
          <div className="relative w-full h-full">
            <Shield className="absolute inset-0 w-full h-full text-white" fill="currentColor" strokeWidth={1.5} />
            {/* Scan lines overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Scan className="w-1/2 h-1/2 text-white/80" strokeWidth={2.5} />
            </div>
            {/* Checkmark badge */}
            <div className="absolute -bottom-0.5 -right-0.5 bg-emerald-500 rounded-full p-0.5">
              <CheckCircle2 className="w-2 h-2 text-white" fill="currentColor" />
            </div>
          </div>
        </div>
      </div>
      {showText && (
        <div className="flex flex-col">
          <span className={cn("font-bold leading-tight", textSizeClasses[size], textColor[variant])}>
            PCI Scan Report
          </span>
          <span className={cn(
            "text-xs leading-tight font-medium",
            variant === "white" ? "text-purple-100" : "text-slate-600 dark:text-slate-400"
          )}>
            Domain Security Monitor
          </span>
        </div>
      )}
    </div>
  );
}

// Standalone icon logo for favicon/compact use
export function LogoIcon({ className, size = "md" }: { className?: string; size?: "sm" | "md" | "lg" }) {
  const sizeClasses = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-12 w-12"
  };

  return (
    <div className={cn("relative", className)}>
      {/* Glow effect */}
      <div className="absolute inset-0 bg-purple-600/20 rounded-xl blur-md"></div>
      {/* Main logo container */}
      <div className={cn(
        "relative flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700 p-1.5 shadow-lg",
        sizeClasses[size]
      )}>
        {/* Shield with scan lines */}
        <div className="relative w-full h-full">
          <Shield className="absolute inset-0 w-full h-full text-white" fill="currentColor" strokeWidth={1.5} />
          {/* Scan lines overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Scan className="w-1/2 h-1/2 text-white/80" strokeWidth={2.5} />
          </div>
          {/* Checkmark badge */}
          <div className="absolute -bottom-0.5 -right-0.5 bg-emerald-500 rounded-full p-0.5">
            <CheckCircle2 className="w-2 h-2 text-white" fill="currentColor" />
          </div>
        </div>
      </div>
    </div>
  );
}

